import type { StateCreator } from 'zustand';
import { landingPageApi, type LPConfig, type LPVariant, type LPAnalytics } from '../services/landingPageApi';

export interface LandingPageSlice {
  lpConfigs: LPConfig[];
  lpVariants: Record<number, LPVariant[]>;
  lpAnalytics: Record<number, LPAnalytics>;
  lpGenerating: boolean;
  lpGeneratingProgress: string | null;
  lpConfigLoading: boolean;
  lpConfigError: string | null;

  fetchLPConfigs: (strategyId: number) => Promise<void>;
  createLPConfig: (data: Parameters<typeof landingPageApi.createConfig>[0]) => Promise<LPConfig>;
  updateLPConfig: (id: number, data: Partial<LPConfig>) => Promise<void>;
  deleteLPConfig: (id: number) => Promise<void>;
  fetchLPVariants: (configId: number) => Promise<void>;
  fetchLPAnalytics: (configId: number) => Promise<void>;
  generateLPVariants: (configId: number, n?: number) => Promise<void>;
  triggerGcof: (configId: number) => Promise<void>;
  concludeExperiment: (configId: number) => Promise<void>;
}

export const createLandingPageSlice: StateCreator<LandingPageSlice> = (set, get) => ({
  lpConfigs: [],
  lpVariants: {},
  lpAnalytics: {},
  lpGenerating: false,
  lpGeneratingProgress: null,
  lpConfigLoading: false,
  lpConfigError: null,

  fetchLPConfigs: async (strategyId) => {
    set({ lpConfigLoading: true, lpConfigError: null });
    try {
      const configs = await landingPageApi.getConfigs(strategyId);
      set({ lpConfigs: configs, lpConfigLoading: false });
    } catch (e) {
      set({ lpConfigError: String(e), lpConfigLoading: false });
    }
  },

  createLPConfig: async (data) => {
    const config = await landingPageApi.createConfig(data);
    set((s) => ({ lpConfigs: [...s.lpConfigs, config] }));
    return config;
  },

  updateLPConfig: async (id, data) => {
    const updated = await landingPageApi.updateConfig(id, data);
    set((s) => ({ lpConfigs: s.lpConfigs.map((c) => (c.id === id ? updated : c)) }));
  },

  deleteLPConfig: async (id) => {
    await landingPageApi.deleteConfig(id);
    set((s) => ({ lpConfigs: s.lpConfigs.filter((c) => c.id !== id) }));
  },

  fetchLPVariants: async (configId) => {
    const variants = await landingPageApi.getVariants(configId);
    set((s) => ({ lpVariants: { ...s.lpVariants, [configId]: variants } }));
  },

  fetchLPAnalytics: async (configId) => {
    const analytics = await landingPageApi.getAnalytics(configId);
    set((s) => ({ lpAnalytics: { ...s.lpAnalytics, [configId]: analytics } }));
  },

  generateLPVariants: async (configId, n = 3) => {
    set({ lpGenerating: true, lpGeneratingProgress: 'Generiere Varianten...' });
    return new Promise((resolve, reject) => {
      const es = landingPageApi.generateVariants(configId, n);
      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data) as { status: string; step?: number; total?: number };
          if (data.status === 'generating') {
            set({ lpGeneratingProgress: `Schritt ${data.step} von ${data.total}...` });
          } else if (data.status === 'done') {
            es.close();
            set({ lpGenerating: false, lpGeneratingProgress: null });
            get().fetchLPVariants(configId);
            resolve();
          }
        } catch { /* ignore parse errors */ }
      };
      es.onerror = () => {
        es.close();
        set({ lpGenerating: false, lpGeneratingProgress: null, lpConfigError: 'Generierung fehlgeschlagen' });
        reject(new Error('Generation failed'));
      };
    });
  },

  triggerGcof: async (configId) => {
    await landingPageApi.triggerGcof(configId);
    await get().fetchLPVariants(configId);
  },

  concludeExperiment: async (configId) => {
    await landingPageApi.concludeExperiment(configId);
    await get().fetchLPVariants(configId);
  },
});
